import { responseRejectInterceptor } from "@api/base";
import { patronApi } from "@api/patrons";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import { Container, Grid, Header } from "semantic-ui-react";
import { PatronResultsList } from "./PatronResultsList";
import Qs from "qs";
import axios from "axios";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export class PatronSearch extends Component {
  modelName = "PATRONS";

  searchApi = new InvenioSearchApi({
    axios: {
      url: patronApi.searchBaseURL,
      withCredentials: true,
    },
    interceptors: {
      response: { reject: responseRejectInterceptor },
    },
  });

  getRequestSerializer = () => {
    const requestSerializer = this.searchApi.requestSerializer;
    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error(
        "RequestSerializer is not configured correctly in PatronSearch."
      );
    }
    return requestSerializer;
  };

  handleExportClick = async (format, size, currentQueryState) => {
    switch (format) {
      case "pdf":
        return this.exportToPDF(currentQueryState, size);
      case "csv":
        return this.handleStandardExport(currentQueryState, size, format);
      default:
        return this.handleStandardExport(currentQueryState, size, "csv");
    }
  };

  handleStandardExport = async (currentQueryState, size, format) => {
    const requestSerializer = this.getRequestSerializer();
    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
    });
    const params = Qs.parse(queryString);
    params["page"] = 1;
    params["format"] = format;

    const exportUrl = `${patronApi.searchBaseURL}?${Qs.stringify(params, {
      arrayFormat: "repeat",
      encode: false,
    })}`;

    window.open(exportUrl, "_blank");
  };

  exportToPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params.page = 1;
      params.size = size;
      const exportUrl = `${patronApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const { data } = await axios.get(exportUrl, { withCredentials: true });
      const hits = data.hits?.hits || [];

      const tableRows = hits
        .map((hit, idx) => {
          const metadata = hit.metadata || {};
          const name = metadata.name || "";
          const email = metadata.email || "";
          const patronId = metadata.id || metadata.patron_id || "";
          const userType = metadata.user_type || metadata.type || "";
          return `
        <tr>
          <td>${idx + 1}</td>
          <td>${name}</td>
          <td>${email}</td>
          <td>${patronId}</td>
          <td>${userType}</td>
        </tr>
      `;
        })
        .join("");

      const tableHeaders = `
      <th>#</th>
      <th>Name</th>
      <th>Email</th>
      <th>Patron ID</th>
      <th>User Type</th>
    `;

      const htmlContent = generateExportHTML({
        reportTitle: "Patrons Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        showUniversityLogo: true,
        showLrcLogo: true,
        fallbackLogoText: "Library Resource Center",
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }
    } catch (error) {
      console.error("PDF export error:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  render() {
    const helperFields = [
      {
        name: "name",
        field: "name",
        defaultValue: '"Doe, John"',
      },
      {
        name: "e-mail",
        field: "email",
      },
    ];

    // Modify initial state to fetch all results
    const initialState = {
      ...setReactSearchKitInitialQueryState(this.modelName),
      size: 1000, // Set a large number to get all results at once
      page: 1,
    };

    const defaultSortingOnEmptyQueryString =
      setReactSearchKitDefaultSortingOnEmptyQueryString(this.modelName);
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">Patrons</Header>
        <OverridableContext.Provider
          value={{
            ...SearchControlsOverridesMap,
            // Use frontend pagination with sorting
            ResultsList: (props) => (
              <PatronResultsList
                {...props}
                showAllResults={false} // Keep pagination but sort all results first
                itemsPerPage={15} // Items per page
              />
            ),
          }}
        >
          <ReactSearchKit
            searchApi={this.searchApi}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSortingOnEmptyQueryString}
          >
            <>
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for patrons"
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Grid>
                <Grid.Row>
                  <ResultsLoader>
                    <Grid.Column width={16}>
                      <Grid columns={1}>
                        <Grid.Column textAlign="right">
                          <ExportReactSearchKitResults
                            exportBaseUrl={patronApi.searchBaseURL}
                            onExportClick={this.handleExportClick}
                            supportedFormats={["csv", "pdf"]}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <Error />
                          {/* Hide the original SearchControls since we're using frontend pagination */}
                          <div style={{ display: "none" }}>
                            <SearchControls
                              modelName={this.modelName}
                              withLayoutSwitcher={false}
                            />
                          </div>
                          <ResultsList
                            renderEmptyResultsElement={() => <EmptyResults />}
                          />
                          {/* Hide the original SearchFooter since we're using frontend pagination */}
                          <div style={{ display: "none" }}>
                            <SearchFooter />
                          </div>
                        </Grid.Column>
                      </Grid>
                    </Grid.Column>
                  </ResultsLoader>
                </Grid.Row>
              </Grid>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
